import type { Variation } from '@shared/types/types.ts';
import { isFirstPage } from '@tilelist/domain/composition/Paging.ts';
import type { ProductItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { sortedColorsWithBestVariationFirst } from '@tilelist/domain/feature-visibility/ColorSorting.ts';

/*                                                                                                    */
type VariationLoaderPort = {
    loadVariations(variationIds: string[]): Promise<Variation[]>;
};

type TeaserBoostQuery = {
    o?: string;
    variationIds?: string[];
};

export type TeaserBoostDecision = {
    /*                                                */
    shouldBoost: boolean;
    /*                                                                                             */
    whatIf: boolean;
};

/**
 *
 *
 *
 */
export function evaluateTeaserBoost(
    onex: Pick<OnEx, 'isExperimentActive' | 'isExperimentGroup'>,
    queryParams: TeaserBoostQuery,
): TeaserBoostDecision {
    const scopeMatches = isFirstPage(queryParams) && !!queryParams.variationIds && queryParams.variationIds.length > 0;

    /*                                                              */
    /*                                                                 */
    const whatIf = scopeMatches && onex.isExperimentActive('TEASER_VARIATION_BOOST');
    /*                                                             */
    /*                 */
    const shouldBoost = scopeMatches && onex.isExperimentGroup('TEASER_VARIATION_BOOST', 'Testgroup');

    return { shouldBoost, whatIf };
}

export async function loadBoostedItems({
    variationIds,
    variationLoadingService,
}: {
    variationIds: string[];
    variationLoadingService: VariationLoaderPort;
}): Promise<ProductItem[]> {
    if (variationIds.length === 0) return [];
    const variations = await variationLoadingService.loadVariations(variationIds);
    return buildBoostedItems(variationIds, variations);
}

export function buildBoostedItems(variationIds: string[], variations: Variation[]): ProductItem[] {
    if (!variationIds.length || !variations.length) return [];

    const variationsById = new Map<string, Variation>(variations.map((v) => [v.variationId, v]));

    const items: ProductItem[] = [];
    for (const variationId of variationIds) {
        const variation = variationsById.get(variationId);
        if (!variation) continue;

        const name = variation.title?.full ?? variation.title?.shortened ?? '';
        const position = items.length + 1;

        items.push({
            variations: [variation],
            currentVariationId: variation.variationId,
            colors: sortedColorsWithBestVariationFirst(variation.colors, variation.variationId),
            localListPosition: position,
            actualListPosition: position,
            originPosition: -1,
            product: {
                productType: 'organic',
                id: variation.variationId,
                bestVariationId: variation.variationId,
                name,
                variationPath: variation.detailPageLink,
            },
            type: 'BOOSTED',
        });
    }
    return items;
}
